import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../../core/layouts/MainLayout';
import StickyActionBar from '../../../core/components/StickyActionBar';
import { ProjectGalleryMini } from '../components/ProjectGalleryMini';
import { UnitsGrid } from '../components/UnitsGrid';
import { ProjectMap } from '../components/ProjectMap';
import { CtaSection } from '../../landing/components/CtaSection';
import { useTranslation } from '../../../core/hooks/useTranslation';
import { MapPin, Maximize, Calendar, Share2 } from 'lucide-react';
import { Button } from '../../../core/components/Button';
import { useProject } from '../../../core/api';
import { useState } from 'react';
import { FloorPlanViewer } from '../components/FloorPlanViewer';

import { resolveImageUrl, getImageDimensions } from '../../../core/utils/image';
import { RemoteImage } from '../../../core/components/RemoteImage';

export const ProjectDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [selectedFloor, setSelectedFloor] = useState<any>(null);
    const [activeFloorId, setActiveFloorId] = useState<string | null>(null);
    const [floorDimensions, setFloorDimensions] = useState<{ w: number; h: number } | null>(null);

    const { data: project, isLoading, error } = useProject(id);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    useEffect(() => {
        if (project?.floors?.length && !activeFloorId) {
            setActiveFloorId(project.floors[0].id);
        }
    }, [project, activeFloorId]);

    useEffect(() => {
        const loadDimensions = async () => {
            const activeFloor = project?.floors?.find((f: any) => f.id === activeFloorId);
            if (activeFloor?.image) {
                try {
                    const resolvedSrc = resolveImageUrl(activeFloor.image);
                    const dims = await getImageDimensions(resolvedSrc);
                    setFloorDimensions(dims);
                } catch (e) {
                    console.error("Failed to load floor image dimensions", e);
                }
            }
        };
        loadDimensions();
    }, [activeFloorId, project]);

    // Loading state
    if (isLoading) {
        return (
            <MainLayout>
                <div className="h-screen flex items-center justify-center bg-surface">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-secondary/20 border-t-secondary rounded-full animate-spin" />
                        <p className="text-slate-500 font-medium">{t('common.loading', 'Loading...')}</p>
                    </div>
                </div>
            </MainLayout>
        );
    }

    if (!project || error) {
        return (
            <div className="h-screen flex items-center justify-center bg-surface">
                <div className="text-center">
                    <h2 className="text-3xl font-display font-bold text-secondary mb-4">{t('errors.projectNotFound', 'Project Not Found')}</h2>
                    <Button onClick={() => navigate('/')} variant="primary">
                        {t('common.goHome', 'Go Home')}
                    </Button>
                </div>
            </div>
        );
    }

    // project is already localized by the backend based on correct locale
    const projectTitle = project.title || project.slug;
    const projectDescription = project.description || '';

    // Area is a localized object from backend ProjectService.formatProject
    const areaName = project.area?.name || project.area?.slug || '';

    // Specs from the project
    const finish = project.finish || '';
    const delivery = project.delivery || '';
    const paymentPlan = project.paymentPlan || '';

    // Build a compatible object for StickyActionBar
    const stickyBarProps = {
        title: projectTitle,
        price: project.price,
        type: 'Project',
        location: areaName
    };

    return (
        <MainLayout>

            {/* 1. Cinematic Hero Section */}
            <div className="relative h-[50vh] sm:h-[65vh] md:h-[80vh] w-full bg-secondary overflow-hidden group">
                <div className="absolute inset-0 transition-transform duration-700 ease-out scale-110 group-hover:scale-100">
                    <RemoteImage
                        src={project.image}
                        alt={projectTitle}
                        className="w-full h-full object-cover opacity-80"
                        containerClassName="w-full h-full"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/40 to-transparent" />
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-24 z-20">
                    <div className="flex items-center gap-3 mb-4 animate-fade-in-up">
                        <span className="text-slate-300 text-xs md:text-sm font-medium uppercase tracking-wider flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {areaName}
                        </span>
                    </div>

                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-6 leading-tight animate-fade-in-up delay-100">
                        {projectTitle}
                    </h1>

                    <div className="flex flex-wrap gap-6 text-white/80 font-medium text-sm md:text-base animate-fade-in-up delay-200">
                        <span className="flex items-center gap-2"><Maximize className="w-5 h-5 text-accent" /> {project.areaRange}</span>
                        {delivery && <span className="flex items-center gap-2"><Calendar className="w-5 h-5 text-accent" /> {t('project.handover', 'Handover')} {delivery}</span>}
                    </div>
                </div>
            </div>

            {/* 2. Sticky Project Header (Title, Info, CTAs) */}
            <StickyActionBar {...stickyBarProps} />

            <div className="bg-surface min-h-screen">
                <div className="container mx-auto px-4 md:px-12 lg:px-24 py-12 md:py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

                        {/* Main Content (Left) */}
                        <div className="lg:col-span-8 space-y-16">
                            {/* Description */}
                            <section>
                                <h3 className="text-2xl font-display font-bold text-secondary mb-6 flex items-center gap-3">
                                    <span className="w-8 h-[2px] bg-secondary block"></span>
                                    {t('project.about', 'About')} {projectTitle}
                                </h3>
                                <div
                                    className="prose prose-lg max-w-none text-slate-600 leading-relaxed font-light"
                                    dangerouslySetInnerHTML={{ __html: projectDescription }}
                                />
                            </section>

                            {/* Gallery */}
                            {project.gallery && project.gallery.length > 0 && (
                                <section>
                                    <h3 className="text-2xl font-display font-bold text-secondary mb-6 flex items-center gap-3">
                                        <span className="w-8 h-[2px] bg-secondary block"></span>
                                        {t('project.gallery', 'Visual Tour')}
                                    </h3>
                                    <ProjectGalleryMini images={project.gallery} />
                                </section>
                            )}



                            {/* Map Section */}
                            {project.latitude && project.longitude && (
                                <section className="mb-12">
                                    <h3 className="text-2xl font-display font-bold text-secondary mb-6 flex items-center gap-3">
                                        <span className="w-8 h-[2px] bg-secondary block"></span>
                                        {t('project.location', 'Location')}
                                    </h3>
                                    <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100 z-0 relative">
                                        <ProjectMap
                                            latitude={project.latitude}
                                            longitude={project.longitude}
                                            boundary={project.boundary || null}
                                            className="h-[400px] w-full"
                                        />
                                    </div>
                                </section>
                            )}

                            {/* Floor Plans Section */}
                            {project.floors && project.floors.length > 0 && (
                                <section className="mb-12">
                                    <h3 className="text-2xl font-display font-bold text-secondary mb-6 flex items-center gap-3">
                                        <span className="w-8 h-[2px] bg-secondary block"></span>
                                        {t('project.floors', 'Floor Plans')}
                                    </h3>

                                    {/* Tabs */}
                                    <div className="flex flex-wrap gap-2 mb-8 border-b border-slate-100 pb-1">
                                        {project.floors.map((floor: any) => (
                                            <button
                                                key={floor.id}
                                                onClick={() => setActiveFloorId(floor.id)}
                                                className={`px-6 py-3 font-display font-bold text-sm uppercase tracking-wider transition-all relative ${activeFloorId === floor.id
                                                    ? 'text-secondary'
                                                    : 'text-slate-400 hover:text-slate-600'
                                                    }`}
                                                role="tab"
                                                aria-selected={activeFloorId === floor.id}
                                            >
                                                {floor.name}
                                                {activeFloorId === floor.id && (
                                                    <span className="absolute bottom-0 left-0 w-full h-[2px] bg-secondary animate-fade-in" />
                                                )}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Active Floor Content */}
                                    <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm p-6 md:p-8 animate-fade-in">
                                        {(() => {
                                            const activeFloor = project.floors.find((f: any) => f.id === activeFloorId) || project.floors[0];
                                            if (!activeFloor) return null;

                                            return (
                                                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                                                    {/* Floor Image (Clickable) */}
                                                    <div
                                                        className="md:col-span-8 bg-slate-50 rounded-xl overflow-hidden relative group cursor-pointer h-[300px] md:h-[400px] flex items-center justify-center p-4 content-center"
                                                        onClick={() => setSelectedFloor(activeFloor)}
                                                    >
                                                        {/* Container for Img + SVG to ensure they overlap perfectly */}
                                                        <div className="relative w-full h-full flex items-center justify-center">
                                                            <RemoteImage
                                                                src={activeFloor.image}
                                                                alt={activeFloor.name}
                                                                className="max-w-full max-h-full object-contain"
                                                            />

                                                            {floorDimensions && (
                                                                <svg
                                                                    viewBox={`0 0 ${floorDimensions.w} ${floorDimensions.h}`}
                                                                    className="absolute inset-0 w-full h-full pointer-events-auto"
                                                                    preserveAspectRatio="xMidYMid meet"
                                                                    style={{ maxHeight: '100%', maxWidth: '100%' }} // Ensure it respects container constraints like img
                                                                >
                                                                    {activeFloor.units?.map((unit: any) => {
                                                                        if (!unit.blueprintShape || unit.blueprintShape.length === 0) return null;
                                                                        // Convert [lat, lng] (y, x) to x,y for SVG, inverting Y to match Leaflet's bottom-left origin (Fix: Invert Y)
                                                                        const points = unit.blueprintShape.map((p: any) => `${p[1]},${floorDimensions.h - p[0]}`).join(' ');

                                                                        const isSold = unit.status === 'SOLD';
                                                                        const isResale = unit.status === 'RESALE';

                                                                        // Calculate center for text label using inverted Y
                                                                        const xs = unit.blueprintShape.map((p: any) => p[1]);
                                                                        const ys = unit.blueprintShape.map((p: any) => floorDimensions.h - p[0]);
                                                                        const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
                                                                        const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;

                                                                        return (
                                                                            <g key={unit.id}>
                                                                                <polygon
                                                                                    points={points}
                                                                                    className={`stroke-[2px] transition-all duration-300 cursor-pointer
                                                                                        ${isSold
                                                                                            ? 'fill-red-500/40 stroke-red-500 hover:fill-red-500/60'
                                                                                            : isResale
                                                                                                ? 'fill-orange-500/40 stroke-orange-500 hover:fill-orange-500/60'
                                                                                                : 'fill-emerald-500/20 stroke-emerald-500 hover:fill-emerald-500/50'
                                                                                        }
                                                                                    `}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        navigate(`/units/${unit.slug}`);
                                                                                    }}
                                                                                >
                                                                                    <title>
                                                                                        {unit.slug} • {unit.type} • {unit.area}m²
                                                                                        {isSold ? ' (Sold Out)' : isResale ? ' (Resale)' : ''}
                                                                                    </title>
                                                                                </polygon>
                                                                                {isSold && (
                                                                                    <text
                                                                                        x={centerX}
                                                                                        y={centerY}
                                                                                        textAnchor="middle"
                                                                                        dominantBaseline="middle"
                                                                                        className="fill-white font-bold text-[10px] sm:text-xs pointer-events-none uppercase tracking-wider"
                                                                                        style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.5)' }}
                                                                                    >
                                                                                        {t('status.sold', 'SOLD OUT')}
                                                                                    </text>
                                                                                )}
                                                                                {isResale && (
                                                                                    <text
                                                                                        x={centerX}
                                                                                        y={centerY}
                                                                                        textAnchor="middle"
                                                                                        dominantBaseline="middle"
                                                                                        className="fill-white font-bold text-[10px] sm:text-xs pointer-events-none uppercase tracking-wider"
                                                                                        style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.5)' }}
                                                                                    >
                                                                                        {t('status.resale', 'RESALE')}
                                                                                    </text>
                                                                                )}
                                                                            </g>
                                                                        );
                                                                    })}
                                                                </svg>
                                                            )}
                                                        </div>

                                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center pointer-events-none">
                                                            <div className="opacity-0 group-hover:opacity-100 bg-white/90 backdrop-blur-sm text-secondary px-4 py-2 rounded-full font-medium text-sm shadow-sm transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                                                                {t('common.viewFullScreen', 'View Full Screen')}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Quick Stats / Side Info */}
                                                    <div className="md:col-span-4 space-y-6">
                                                        <div>
                                                            <h4 className="font-display font-bold text-2xl text-secondary mb-2">{activeFloor.name}</h4>
                                                            <p className="text-slate-500 text-sm">{t('project.floorDescription', 'View the layout and available units for this floor.')}</p>
                                                        </div>

                                                        <div className="space-y-4">
                                                            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                                                                <span className="text-slate-500 text-sm font-medium">{t('project.unitsAvailable', 'Units Available')}</span>
                                                                <span className="font-bold text-secondary bg-white px-2 py-1 rounded shadow-sm">
                                                                    {activeFloor.units?.length || 0}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <Button
                                                            onClick={() => setSelectedFloor(activeFloor)}
                                                            variant="outline"
                                                            className="w-full"
                                                        >
                                                            {t('common.viewPlan', 'View Floor Plan')}
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </section>
                            )}

                            {project.units && project.units.length > 0 && (
                                <section>
                                    <UnitsGrid
                                        units={project.units.map((u: any) => ({
                                            ...u,
                                            title: u.title || u.slug
                                        }))}
                                        projectName={projectTitle}
                                        deliveryDate={delivery}
                                        projectFinish={finish}
                                        title={t('project.availableUnits', 'Available Units')}
                                        viewAllLink={`/search?project=${project.slug}`}
                                    />
                                </section>
                            )}
                        </div>

                        {/* Sidebar (Right) */}
                        <div className="lg:col-span-4">
                            <div className="sticky top-28 space-y-6">
                                <div className="bg-white border border-slate-200 p-8 shadow-sm">
                                    <h4 className="font-display font-bold text-secondary text-xl mb-6 pb-4 border-b border-slate-100 uppercase tracking-wider">
                                        {t('project.highlights', 'Project Highlights')}
                                    </h4>

                                    <div className="space-y-5">
                                        {finish && (
                                            <div className="flex justify-between items-center group">
                                                <span className="text-slate-500 text-sm font-medium">{t('project.finish', 'Finishing')}</span>
                                                <span className="font-bold text-secondary">{finish}</span>
                                            </div>
                                        )}
                                        {delivery && (
                                            <div className="flex justify-between items-center group">
                                                <span className="text-slate-500 text-sm font-medium">{t('project.delivery', 'Delivery')}</span>
                                                <span className="font-bold text-secondary">{delivery}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between items-center group">
                                            <span className="text-slate-500 text-sm font-medium">{t('project.area', 'Area')}</span>
                                            <span className="font-bold text-secondary">{project.areaRange}</span>
                                        </div>

                                        {paymentPlan && (
                                            <div className="mt-4 pt-4 border-t border-slate-50">
                                                <p className="text-xs text-slate-400 uppercase tracking-widest font-bold mb-2">{t('project.paymentPlan', 'Payment Plan')}</p>
                                                <p className="text-xl font-display font-bold text-secondary leading-tight">{paymentPlan}</p>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-8 flex justify-center gap-4">
                                        <button
                                            onClick={() => {
                                                if (navigator.share) {
                                                    navigator.share({
                                                        title: projectTitle,
                                                        text: `Check out ${projectTitle} on El Hesn`,
                                                        url: window.location.href,
                                                    }).catch(console.error);
                                                } else {
                                                    navigator.clipboard.writeText(window.location.href);
                                                    alert(t('common.linkCopied', 'Link copied to clipboard'));
                                                }
                                            }}
                                            className="text-slate-400 hover:text-secondary transition-colors p-2 flex items-center gap-2"
                                        >
                                            <Share2 className="w-5 h-5" />
                                            <span className="text-sm">{t('common.share', 'Share')}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <CtaSection />

            {/* Floor Plan Viewer Modal */}
            {selectedFloor && (
                <FloorPlanViewer
                    floor={selectedFloor}
                    units={selectedFloor.units || []}
                    onClose={() => setSelectedFloor(null)}
                />
            )}
        </MainLayout>
    );
};
